import { customElement, property } from "lit/decorators.js";
import { ToolbarDropdown } from "@omegagrid/toolbar";
import { style } from "./sqlEditor.style";
import { ScriptEditorAdapter, ScriptResult } from "./scriptEditorAdapter";
import { CodeEditor } from "./codeEditor";
import { actions, dom, log } from "@omegagrid/core";
import { ToolbarButton } from "@omegagrid/toolbar";
import { ToolbarItemOptions } from "../types";

export class ScriptEditorEvent extends Event {

	result: ScriptResult;
	code: string;

	constructor(type: string, args: Partial<ScriptEditorEvent>) {
		super(`scripteditor.${type}`, {composed: true, bubbles: true});
		Object.assign(this, args);
	}

}

function formatDuration(ms: number) {
	const minutes = Math.floor(ms / 60000);
	const seconds = Math.floor((ms % 60000) / 1000);
	const milliseconds = Math.floor(ms % 1000);
	return [
	  ((minutes < 10 ? '0' : '') + minutes).toString(),
	  ((seconds < 10 ? '0' : '') + seconds).toString(),
	  ((milliseconds < 100 ? (milliseconds < 10 ? '00' : '0') : '') + milliseconds).toString()
	].join(':');
  }

@customElement('rp-editor-script')
export class ScriptEditor extends CodeEditor {

	static styles = [...CodeEditor.styles, style];

	@property({type: Object})
	adapter: ScriptEditorAdapter;

	private timer: number;
	private _hasExecButton = false;

	async willUpdate(props: Map<PropertyKey, unknown>) {
		await super.willUpdate(props);
		// if (this.options) this.options.language = ;
	}

	async createToolbarItems() {
		const options = await this.adapter.getOptions();
		let items: ToolbarItemOptions[] = [];

		for (const key in options.toolbar) {
			const itemDef = options.toolbar[key as keyof typeof options.toolbar];
			if (itemDef.enabled === false) continue;
			if (key === 'exec' && !itemDef.disabled) this._hasExecButton = true;
			const item = {id: key, ...itemDef} as ToolbarItemOptions;
			items.push(item);
		}

		items.sort((a, b) => (a.ord || 0) - (b.ord || 0));
		return items;
	}

	startTimer(button: ToolbarButton) {
		const durationElm = button.querySelector('#duration') as HTMLSpanElement; 
		const start = window.performance.now();
		this.timer = window.setInterval(() => {
			durationElm.innerText = formatDuration(window.performance.now() - start);
		}, 10);
	}

	endTimer() {
		if (this.timer != null) {
			window.clearInterval(this.timer);
			this.timer = null;
		}
	}

	async exec() {
		const execButton = this.toolbar.controls.get('exec') as ToolbarButton;
		const stopButton = this.toolbar.controls.get('stop') as ToolbarButton;
		
		dom.hideElement(execButton);
		dom.showElement(stopButton);
		this.startTimer(stopButton);
		
		await this.adapter.exec(this.editor.selectedContent, ({ result }) => {
			this.dispatchEvent(new ScriptEditorEvent('result', { result }));
		});

		this.endTimer();
		dom.showElement(execButton, 'flex');
		dom.hideElement(stopButton);
	}

	abort() {
		this.adapter.abort();
	}

	async ready() {
		await super.ready();
		
		// additional keyboard shortcut using monaco editor onKeyDown
		this.editor.editor.onKeyDown((e) => {
			if (this._hasExecButton && e.ctrlKey && e.code === 'KeyB') {
				e.preventDefault();
				e.stopPropagation();
				this.exec();
			}
		});
		
	}

	
	async _onToolbarAction(e: actions.ActionEvent) {
		switch (e.action.key) {
			case 'exec': this.exec(); break;
			case 'stop': this.abort(); break;
		}
		await super._onToolbarAction(e);
	}

}
